import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import i18n from '../i18n';

// Create a simple context for i18n
export const I18nContext = createContext();

export const CustomI18nProvider = ({ children }) => {
  const [i18nReady, setI18nReady] = useState(i18n.isInitialized);
  const [, setLanguageVersion] = useState(0);

  useEffect(() => {
    console.log('I18n Provider mounting - initialized:', i18n.isInitialized);
    console.log('Current language:', i18n.language);
    console.log('Available resources:', i18n.options?.resources ? Object.keys(i18n.options.resources) : 'none');
    
    // Listen for language changes to trigger re-renders
    const handleLanguageChanged = () => {
      console.log('Language changed to:', i18n.language);
      setI18nReady(true);
      setLanguageVersion((prev) => prev + 1);
    };

    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  return (
    <I18nContext.Provider value={{ i18n, i18nReady }}>
      {children}
    </I18nContext.Provider>
  );
};

// Custom useTranslation hook that works with our context
export const useTranslation = () => {
  const context = useContext(I18nContext);
  
  if (!context) {
    throw new Error('useTranslation must be used within CustomI18nProvider');
  }

  const { i18n: i18nInstance } = context;
  // Force re-render when language changes
  const [languageVersion, setLanguageVersion] = useState(0);

  useEffect(() => {
    const handleLanguageChanged = () => {
      setLanguageVersion((prev) => prev + 1);
    };

    i18nInstance.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChanged);
    };
  }, [i18nInstance]);

  const t = useCallback(
    (key, options = {}) => {
      const result = i18nInstance.t(key, options);
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18nInstance, languageVersion]
  );

  // Helper function to get raw resource data (for arrays, objects, etc.)
  const getResource = useCallback(
    (key) => {
      const keys = key.split('.');
      let resource = i18nInstance.getResourceBundle(i18nInstance.language);
      
      for (const k of keys) {
        resource = resource?.[k];
        if (resource === undefined) {
          return undefined;
        }
      }
      
      return resource;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18nInstance, languageVersion]
  );

  const changeLanguage = useCallback(
    (lang) => {
      return i18nInstance.changeLanguage(lang);
    },
    [i18nInstance]
  );

  return {
    t,
    getResource,
    i18n: i18nInstance,
    ready: i18nInstance.isInitialized,
    language: i18nInstance.language,
    changeLanguage,
  };
};

